import React, { useState, useRef, useEffect, useMemo } from "react";
import { Box, CircularProgress, IconButton, InputBase, List, ListItemButton, ListItemText, Paper, Typography, alpha } from "@mui/material";
import { Send, Close, Reply } from "@mui/icons-material";
import { useThemeContext } from "../../../../core/contexts/ThemeContext";
import { cores, gradienteMarca, raio, superficieModo } from "../identidade";
import { IMensagemRapidaWhatsApp } from "../../../../marketplace/interfaces/IMensagemRapidaWhatsApp";
import { IMensagemRespondida } from "../../../interface/atendimento/IMensagem";
import { TipoEmissorMensagem } from "../../../enum/atendimento/TipoEmissorMensagem.enum";
import { loadRascunho, saveRascunho } from "../../../hooks/atendimento/useChatPdVPersistence";

interface MessageInputProps {
    chatUuid?: string;
    onSendMessage: (message: string) => Promise<void>;
    loading?: boolean;
    disabled?: boolean;
    mensagensRapidas?: IMensagemRapidaWhatsApp[];
    replyTo?: IMensagemRespondida & { texto?: string };
    onCancelReply?: () => void;
}

const MessageInput: React.FC<MessageInputProps> = ({ chatUuid, onSendMessage, loading = false, disabled = false, mensagensRapidas = [], replyTo, onCancelReply }) => {
    const [message, setMessage] = useState(() => chatUuid ? loadRascunho(chatUuid) : "");
    const [selectedIndex, setSelectedIndex] = useState(0);
    const { themeName } = useThemeContext();
    const listRef = useRef<HTMLUListElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const messageRef = useRef(message);
    useEffect(() => { messageRef.current = message; }, [message]);

    const quickMenuAberto = message.startsWith("/") && mensagensRapidas.length > 0;
    const filtro = quickMenuAberto ? message.slice(1).toLowerCase() : "";
    const mensagensRapidasFiltradas = useMemo(() => {
        if (!quickMenuAberto) return [];
        return mensagensRapidas.filter(m => m.chave.toLowerCase().includes(filtro));
    }, [quickMenuAberto, mensagensRapidas, filtro]);

    // Persiste rascunho local (debounced 500ms) — evita cascata de re-render no pai a cada keystroke.
    useEffect(() => {
        if (!chatUuid) return;
        const timer = setTimeout(() => {
            saveRascunho(chatUuid, message);
        }, 500);
        return () => clearTimeout(timer);
    }, [message, chatUuid]);

    // Flush final ao desmontar (troca de chat antes do debounce disparar)
    useEffect(() => {
        return () => {
            if (chatUuid) {
                saveRascunho(chatUuid, messageRef.current);
            }
        };
    }, [chatUuid]);

    useEffect(() => {
        setSelectedIndex(0);
    }, [filtro]);

    useEffect(() => {
        if (listRef.current && mensagensRapidasFiltradas.length > 0) {
            const item = listRef.current.children[selectedIndex] as HTMLElement;
            item?.scrollIntoView({ block: "nearest" });
        }
    }, [selectedIndex, mensagensRapidasFiltradas.length]);

    // Focar input quando reply for definido
    useEffect(() => {
        if (replyTo) {
            inputRef.current?.focus();
        }
    }, [replyTo]);

    const handleSend = async () => {
        if (!message.trim() || loading || disabled) return;

        const messageToSend = message;
        setMessage("");
        if (chatUuid) saveRascunho(chatUuid, "");

        await onSendMessage(messageToSend);
    };

    const handleSelecionar = (mensagem: IMensagemRapidaWhatsApp) => {
        setMessage(mensagem.mensagem);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (quickMenuAberto && mensagensRapidasFiltradas.length > 0) {
            if (e.key === "ArrowDown") {
                e.preventDefault();
                setSelectedIndex(prev => Math.min(prev + 1, mensagensRapidasFiltradas.length - 1));
                return;
            }
            if (e.key === "ArrowUp") {
                e.preventDefault();
                setSelectedIndex(prev => Math.max(prev - 1, 0));
                return;
            }
            if (e.key === "Enter") {
                e.preventDefault();
                handleSelecionar(mensagensRapidasFiltradas[selectedIndex]);
                return;
            }
            if (e.key === "Escape") {
                e.preventDefault();
                setMessage("");
                return;
            }
        }

        if (e.key === "Escape" && replyTo) {
            e.preventDefault();
            onCancelReply?.();
            return;
        }

        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    return (
        <Box sx={{ position: "relative", width: "100%" }}>
            {quickMenuAberto && mensagensRapidasFiltradas.length > 0 && (
                <Paper
                    elevation={8}
                    sx={{
                        position: "absolute",
                        bottom: "100%",
                        left: 0,
                        right: 0,
                        maxHeight: 240,
                        overflow: "hidden",
                        zIndex: 1300,
                        borderRadius: "8px 8px 0 0",
                    }}
                >
                    <List
                        ref={listRef}
                        dense
                        disablePadding
                        sx={{ maxHeight: 192, overflow: "auto" }}
                    >
                        {mensagensRapidasFiltradas.map((mensagem, index) => (
                            <ListItemButton
                                key={mensagem.chave}
                                selected={index === selectedIndex}
                                onMouseEnter={() => setSelectedIndex(index)}
                                onClick={() => handleSelecionar(mensagem)}
                                sx={{
                                    px: 1.5,
                                    "&.Mui-selected": {
                                        backgroundColor: alpha(cores.marca[500], themeName === "dark" ? 0.24 : 0.12),
                                    },
                                }}
                            >
                                <ListItemText
                                    primary={
                                        <Typography variant="body2" fontWeight="bold" component="span">
                                            /{mensagem.chave}
                                        </Typography>
                                    }
                                    secondary={
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            component="span"
                                            sx={{
                                                display: "block",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {mensagem.mensagem}
                                        </Typography>
                                    }
                                />
                            </ListItemButton>
                        ))}
                    </List>
                </Paper>
            )}

            {/* Painel de resposta — estilo WhatsApp */}
            {replyTo && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1.5,
                        py: 1,
                        backgroundColor: superficieModo(themeName, 0.04),
                        borderTop: `1px solid ${superficieModo(themeName, 0.09)}`,
                    }}
                >
                    {/* Barra colorida + conteúdo */}
                    <Box
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            display: 'flex',
                            borderLeft: `3px solid ${cores.marca[500]}`,
                            borderRadius: '4px',
                            backgroundColor: superficieModo(themeName, 0.06),
                            px: 1,
                            py: 0.5,
                            gap: 0.5,
                        }}
                    >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                                variant="caption"
                                sx={{ fontWeight: 700, color: cores.marca[500], display: 'block', lineHeight: 1.4 }}
                            >
                                {replyTo.emissor === TipoEmissorMensagem.CLIENTE ? 'Cliente' : 'Você'}
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{
                                    display: 'block',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    color: 'text.secondary',
                                    lineHeight: 1.4,
                                }}
                            >
                                {replyTo.texto || 'Mensagem'}
                            </Typography>
                        </Box>
                    </Box>

                    <IconButton size="small" onClick={onCancelReply} sx={{ flexShrink: 0, color: 'text.secondary' }}>
                        <Close sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>
            )}

            <Paper
                elevation={0}
                sx={{
                    px: 2,
                    py: 1.5,
                    display: "flex",
                    alignItems: "flex-end",
                    gap: 1,
                    borderRadius: 0,
                    borderTop: theme => `1px solid ${superficieModo(themeName, 0.09)}`,
                    backgroundImage: "none",
                    width: "100%",
                    flexShrink: 0,
                }}
            >
                <InputBase
                    inputRef={inputRef}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Digite / para mensagens rápidas"
                    disabled={loading || disabled}
                    multiline
                    maxRows={4}
                    sx={{
                        flex: 1,
                        px: 1.5,
                        py: 1,
                        backgroundColor: superficieModo(themeName, 0.05),
                        border: `1px solid ${superficieModo(themeName, 0.08)}`,
                        borderRadius: raio.sm,
                        fontSize: "12.5px",
                        lineHeight: 1.5,
                        transition: "border-color .16s, background-color .16s",
                        "&.Mui-focused": {
                            borderColor: alpha(cores.marca[500], 0.45),
                            backgroundColor: alpha(cores.marca[500], 0.05),
                        },
                    }}
                />
                <IconButton
                    onClick={handleSend}
                    disabled={!message.trim() || loading || disabled}
                    sx={{
                        width: 38,
                        height: 38,
                        flexShrink: 0,
                        borderRadius: raio.sm,
                        color: "#fff",
                        background: gradienteMarca,
                        transition: "opacity .16s, transform .16s",
                        "&:hover": { background: gradienteMarca, transform: "translateY(-1px)" },
                        // Sem isto o gradiente (background-image) fica por baixo do
                        // cinza do MUI e o ícone desabilitado some no azul.
                        "&.Mui-disabled": {
                            background: "none",
                            backgroundColor: superficieModo(themeName, 0.12),
                            color: "text.disabled",
                        },
                    }}
                >
                    {loading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <Send sx={{ fontSize: 18 }} />}
                </IconButton>
            </Paper>
        </Box>
    );
};

export default MessageInput;
